import React, { useState, useRef } from 'react';

interface OcrImportProps {
  onSuggestion: (germanName: string, botanicalName: string) => void;
  onClose: () => void;
}

export const OcrImport: React.FC<OcrImportProps> = ({ onSuggestion, onClose }) => {
  const [lines, setLines] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [german, setGerman] = useState('');
  const [botanical, setBotanical] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('Nur Bilder erlaubt');
      return;
    }
    setError(null);
    setLines([]);
    setLoading(true);
    setProgress(0);

    try {
      // Dynamically import Tesseract to avoid bundle bloat on initial load
      const { createWorker } = await import('tesseract.js');
      const worker = await createWorker('deu+eng', 1, {
        logger: (m: { status: string; progress: number }) => {
          if (m.status === 'recognizing text') {
            setProgress(Math.round(m.progress * 100));
          }
        },
      });
      const { data } = await worker.recognize(file);
      await worker.terminate();

      const extracted = data.text
        .split('\n')
        .map((l: string) => l.trim())
        .filter((l: string) => l.length > 2);

      setLines(extracted);
    } catch (err) {
      setError('OCR fehlgeschlagen: ' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = () => {
    if (german || botanical) {
      onSuggestion(german.trim(), botanical.trim());
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-bg/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative panel w-full max-w-lg p-6 flex flex-col gap-5 animate-fadeIn">
        <div className="flex items-center justify-between">
          <h2 className="plant-name text-xl font-semibold text-cream">Namen aus Foto importieren</h2>
          <button onClick={onClose} className="btn-icon" aria-label="Schließen">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <p className="text-creamDim/70 text-sm">
          Lade ein Foto einer handgeschriebenen oder gedruckten Pflanzenliste hoch. Tesseract.js erkennt den Text automatisch.
        </p>

        {/* File picker */}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          id="ocr-file-input"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
        />
        <label
          htmlFor="ocr-file-input"
          className="flex items-center justify-center gap-3 h-28 rounded-xl border-2 border-dashed border-border hover:border-sage/60 bg-surface cursor-pointer transition-colors"
        >
          {loading ? (
            <div className="flex flex-col items-center gap-2">
              <div className="w-6 h-6 border-2 border-sage border-t-transparent rounded-full animate-spin" />
              <span className="text-xs text-creamDim">Erkenne Text… {progress}%</span>
              <div className="w-32 progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }} />
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1 text-center">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-sageDark">
                <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21 15 16 10 5 21"/>
              </svg>
              <span className="text-sm text-creamDim">Foto auswählen</span>
            </div>
          )}
        </label>

        {error && <p className="text-red-400 text-xs">{error}</p>}

        {/* Detected lines */}
        {lines.length > 0 && (
          <div className="flex flex-col gap-2">
            <p className="text-xs text-creamDim/70 font-mono uppercase tracking-wider">Erkannte Zeilen — klicken zum Übernehmen:</p>
            <div className="max-h-36 overflow-y-auto flex flex-col gap-1">
              {lines.map((line, i) => (
                <button
                  key={i}
                  onClick={() => {
                    if (!german) setGerman(line);
                    else setBotanical(line);
                  }}
                  className="text-left px-3 py-1.5 rounded-lg bg-surface hover:bg-border/40 text-cream text-sm transition-colors border border-transparent hover:border-border"
                >
                  {line}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Manual correction fields */}
        {lines.length > 0 && (
          <div className="flex flex-col gap-3">
            <div>
              <label className="text-xs text-creamDim/70 mb-1 block">Deutscher Name</label>
              <input
                className="input"
                value={german}
                onChange={(e) => setGerman(e.target.value)}
                placeholder="Deutscher Pflanzenname…"
              />
            </div>
            <div>
              <label className="text-xs text-creamDim/70 mb-1 block">Botanischer Name</label>
              <input
                className="input botanical"
                value={botanical}
                onChange={(e) => setBotanical(e.target.value)}
                placeholder="Botanischer Name…"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={onClose} className="btn-ghost">Abbrechen</button>
              <button onClick={handleAccept} className="btn-primary" disabled={!german && !botanical}>
                Namen übernehmen
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
